import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { CHOICE_SEPARATOR } from '@Components/Agents/Conversation/ChoiceGrid';
import {
  AgentConversationInteractionsResponse,
  AgentInteraction,
  AgentInteractionThinkingStep,
  AgentOptionItem,
  OSAMessage,
  ThinkingStep,
} from '@Models';
import { bopsApi } from '@Network';
import { toCardFile } from '../../Helpers/Agents/attachments';
import { fromApiThinkingSteps } from '../../Helpers/Agents/thinkingSteps';

/**
 * Derive which option the user picked (if any) by matching the *next*
 * interaction's user input against this interaction's option labels.
 * Avoids mutating the cache: the selection is computed on each render from
 * the conversation history itself.
 */
const deriveSelectedOptionId = (
  options: AgentOptionItem[] | undefined,
  nextInteraction: AgentInteraction | undefined,
): string | undefined => {
  if (!options || options.length === 0) return undefined;
  const nextInput = nextInteraction?.content?.input?.trim();
  if (!nextInput) return undefined;
  return options.find((opt) => opt.label === nextInput)?.id;
};

/**
 * Multi-select analogue of deriveSelectedOptionId. The confirm action submits
 * the chosen labels joined by newlines, so we split the next input on newlines
 * and match each line back to an option label.
 */
const deriveSelectedOptionIds = (
  options: AgentOptionItem[] | undefined,
  nextInteraction: AgentInteraction | undefined,
): string[] | undefined => {
  if (!options || options.length === 0) return undefined;
  const nextInput = nextInteraction?.content?.input?.trim();
  if (!nextInput) return undefined;
  const lines = new Set(nextInput.split('\n').map((s) => s.trim()));
  const ids = options.filter((opt) => lines.has(opt.label)).map((opt) => opt.id);
  return ids.length > 0 ? ids : undefined;
};

// Keyed by the raw array so unchanged interactions keep a stable steps identity (MessageAgent is memoized).
const thinkingStepsCache = new WeakMap<AgentInteractionThinkingStep[], ThinkingStep[]>();

const mapThinkingSteps = (raw: AgentInteractionThinkingStep[] | null | undefined): ThinkingStep[] => {
  if (!raw) return [];
  const cached = thinkingStepsCache.get(raw);
  if (cached) return cached;
  const mapped = fromApiThinkingSteps(raw);
  thinkingStepsCache.set(raw, mapped);
  return mapped;
};

/** Shared so every writer of the interactions cache keys it the same way. */
export const INTERACTION_LIMIT = 100;

/**
 * Choice-grid analogue. Confirm submits one `<row label> → <answer>` line per
 * ANSWERED row, so we anchor on the row labels we already know rather than
 * splitting on the separator — a label may legitimately contain one, and a
 * mis-parse here would show the user an answer they never gave.
 *
 * Each line is consumed once. Two rows may legitimately share a label -- the
 * same product escalated at two DCs -- and ids, not labels, are what keep them
 * apart; matching greedily would give both rows the first row's answer. Rows
 * are walked in submission order, so first-unconsumed-match is the right one.
 *
 * Rows with no matching line stay absent: unanswered is not an answer.
 */
const deriveChoicegridAnswers = (
  rows: AgentOptionItem[] | undefined,
  nextInteraction: AgentInteraction | undefined,
): Record<string, string> | undefined => {
  if (!rows || rows.length === 0) return undefined;
  const nextInput = nextInteraction?.content?.input?.trim();
  if (!nextInput) return undefined;
  const lines = nextInput.split('\n').map((s) => s.trim());
  const consumed = new Set<number>();
  const answers: Record<string, string> = {};
  rows.forEach((row) => {
    const prefix = `${row.label}${CHOICE_SEPARATOR}`;
    const idx = lines.findIndex((l, i) => !consumed.has(i) && l.startsWith(prefix));
    if (idx === -1) return;
    consumed.add(idx);
    const answerLabel = lines[idx].slice(prefix.length).trim();
    const choice = row.choices?.find((c) => c.label === answerLabel);
    if (choice) answers[row.id] = choice.id;
  });
  return Object.keys(answers).length > 0 ? answers : undefined;
};

export const convertInteractionToMessages = (
  interaction: AgentInteraction,
  nextInteraction?: AgentInteraction,
): OSAMessage[] => {
  const messages: OSAMessage[] = [];

  // Convert timestamp to Date - handle both numeric string and ISO string formats
  const timestamp = Number.isNaN(Number(interaction.timestamp))
    ? new Date(interaction.timestamp)
    : new Date(Number(interaction.timestamp));

  // User message
  messages.push({
    id: `${interaction.conversation_id}_${interaction.timestamp}_user`,
    role: 'user',
    content: interaction.content.input,
    timestamp,
    conversationId: interaction.conversation_id,
    interactionTimestamp: interaction.timestamp,
    ...(interaction.attachments?.length
      ? {
          attachments: interaction.attachments.map(toCardFile),
          attachmentsUploading: interaction.attachments_uploading,
        }
      : {}),
  });

  // Assistant message - only add if there's output content OR interactive options
  const assistantContent = interaction.content.output;
  const assistantOptions = interaction.options;
  const assistantMultiselect = interaction.multiselect_options;
  const assistantChoicegrid = interaction.choicegrid_options;
  const hasOutput = assistantContent && assistantContent.trim();
  const hasOptions = assistantOptions && assistantOptions.length > 0;
  const hasMultiselect = assistantMultiselect && assistantMultiselect.length > 0;
  const hasChoicegrid = assistantChoicegrid && assistantChoicegrid.length > 0;
  if (hasOutput || hasOptions || hasMultiselect || hasChoicegrid) {
    const thinkingSteps = mapThinkingSteps(interaction.thinking_steps);
    const thinkingSeconds = interaction.thinking_seconds;
    messages.push({
      id: `${interaction.conversation_id}_${interaction.timestamp}_assistant`,
      role: 'assistant',
      content: assistantContent,
      timestamp,
      metadata: {
        agent_metadata: interaction.agent_metadata,
      },
      feedback: interaction.feedback,
      conversationId: interaction.conversation_id,
      interactionTimestamp: interaction.timestamp,
      generatedFiles: interaction.generated_files,
      options: assistantOptions,
      selectedOptionId: deriveSelectedOptionId(assistantOptions, nextInteraction),
      multiselectOptions: assistantMultiselect,
      selectedOptionIds: deriveSelectedOptionIds(assistantMultiselect, nextInteraction),
      choicegridOptions: assistantChoicegrid,
      choicegridAnswers: deriveChoicegridAnswers(assistantChoicegrid, nextInteraction),
      ...(thinkingSteps.length > 0 ? { thinkingSteps } : {}),
      ...(typeof thinkingSeconds === 'number' && thinkingSeconds > 0 ? { thinkingSeconds } : {}),
    });
  }

  return messages;
};

export const useAgentInteractions = (conversationId: string | null, limit = INTERACTION_LIMIT) => {
  const query = useQuery<AgentConversationInteractionsResponse>({
    queryKey: ['agent-conversation-interactions', conversationId, limit],
    queryFn: () => bopsApi.getAgentConversationInteractions(conversationId || '', limit),
    enabled: !!conversationId,
    staleTime: 30 * 1000, // 30 seconds
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 403 || status === 404) return false;
      return failureCount < 3;
    },
  });

  // Convert interactions to messages (memoized).
  // Each interaction is paired with the next so the assistant's
  // `selectedOptionId` can be derived from the user's follow-up input.
  const messages = useMemo(() => {
    const interactions = query.data?.interactions;
    if (!interactions) return [];
    return interactions.flatMap((interaction, index) =>
      convertInteractionToMessages(interaction, interactions[index + 1]),
    );
  }, [query.data?.interactions]);

  const isAccessDenied = query.isError && (query.error as { response?: { status?: number } })?.response?.status === 403;

  return {
    messages,
    isAccessDenied,
    interactionsQuery: query,
  };
};

export default useAgentInteractions;
